import { useState } from 'react';
import { Button } from '@/components/base/Button';
import { Skeleton } from '@/components/base/Skeleton';
import { formatGenderDisplay } from '@/lib/types/patient';
import { formatLabel } from '@/lib/utils/format';
import type { MaritalStatus } from '@/lib/types/patient';
import { Loader2, CheckCircle2, CalendarPlus } from 'lucide-react';
import { sanitizePhoneInput } from '@/lib/utils/phone';

// Shared input styles for different form variants
const INPUT_STYLES = {
  modal:
    'flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600',
  page: 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
} as const;

const LABEL_STYLES = {
  modal: 'mb-1.5 block font-hanken text-sm font-medium text-gray-700',
  page: 'mb-1.5 block text-sm font-medium text-foreground',
} as const;

type FormVariant = 'modal' | 'page';

// Reusable Age and DOB fields component
type AgeDobFieldsProps = {
  variant: FormVariant;
  ageValue: string;
  dateOfBirth: string;
  onAgeChange: (value: string) => void;
  onDobChange: (value: string) => void;
  disabled: boolean;
  errors?: { age?: string; dateOfBirth?: string };
};

function AgeDobFields({
  variant,
  ageValue,
  dateOfBirth,
  onAgeChange,
  onDobChange,
  disabled,
  errors,
}: AgeDobFieldsProps) {
  const inputClass = INPUT_STYLES[variant];
  const labelClass = LABEL_STYLES[variant];

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Age Input */}
      <div className={variant === 'page' ? 'space-y-1.5' : ''}>
        <label className={labelClass}>Age (years)</label>
        <input
          type="number"
          placeholder="Enter age"
          value={ageValue}
          onChange={(e) => onAgeChange(e.target.value)}
          disabled={disabled}
          min={0}
          max={150}
          className={inputClass}
        />
        {errors?.age && <p className="mt-1.5 text-sm text-destructive">{errors.age}</p>}
      </div>

      {/* Date of Birth Input - no label */}
      <div className={variant === 'page' ? 'space-y-1.5' : ''}>
        <div className={labelClass}>&nbsp;</div>
        <input
          type="date"
          value={dateOfBirth}
          onChange={(e) => onDobChange(e.target.value)}
          disabled={disabled}
          className={inputClass}
        />
        {errors?.dateOfBirth && (
          <p className="mt-1.5 text-sm text-destructive">{errors.dateOfBirth}</p>
        )}
      </div>
    </div>
  );
}

// Reusable Gender field component
type GenderFieldProps = {
  variant: FormVariant;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled: boolean;
  error?: string;
};

function GenderField({ variant, value, options, onChange, disabled, error }: GenderFieldProps) {
  const inputClass = `${INPUT_STYLES[variant]} appearance-none`;
  const labelClass = LABEL_STYLES[variant];

  return (
    <div>
      <label htmlFor="gender" className={labelClass}>
        Gender
        <span className="ml-1 text-destructive" aria-label="required">
          *
        </span>
      </label>
      <select
        id="gender"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={inputClass}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1.5 text-sm text-destructive">{error}</p>}
    </div>
  );
}

// Reusable Marital Status field component
type MaritalStatusFieldProps = {
  variant: FormVariant;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled: boolean;
  error?: string;
};

function MaritalStatusField({
  variant,
  value,
  options,
  onChange,
  disabled,
  error,
}: MaritalStatusFieldProps) {
  const inputClass = `${INPUT_STYLES[variant]} appearance-none`;
  const labelClass = LABEL_STYLES[variant];

  return (
    <div>
      <label htmlFor="maritalStatus" className={labelClass}>
        Marital Status
      </label>
      <select
        id="maritalStatus"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={inputClass}
      >
        <option value="">Select Marital Status</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1.5 text-sm text-destructive">{error}</p>}
    </div>
  );
}

// Reusable Address field component
type AddressFieldProps = {
  variant: FormVariant;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  error?: string;
};

function AddressField({ variant, value, onChange, disabled, error }: AddressFieldProps) {
  const textareaClass =
    variant === 'modal'
      ? 'flex w-full resize-none items-center rounded-lg border-0 bg-[#fbfafd] px-3 py-2 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600'
      : 'flex w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground';
  const labelClass = LABEL_STYLES[variant];

  return (
    <div>
      <label htmlFor="address" className={labelClass}>
        Address
      </label>
      <textarea
        id="address"
        placeholder="Enter full address"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={2}
        className={textareaClass}
      />
      {error && <p className="mt-1.5 text-sm text-destructive">{error}</p>}
    </div>
  );
}

export type PatientFormViewProps = {
  mode: 'create' | 'edit';
  values: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    weight?: string;
    gender: 'M' | 'F' | 'O' | 'U';
    maritalStatus?: MaritalStatus;
    phoneNumber: string;
    address?: string;
    identifier?: string;
  };
  errors?: Partial<{
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    age: string;
    gender: string;
    maritalStatus: string;
    phoneNumber: string;
    address: string;
    identifier: string;
    weight: string;
  }>;
  options: {
    gender: Array<{ value: 'M' | 'F' | 'O' | 'U'; label: string }>;
    maritalStatus?: Array<{ value: 'S' | 'M' | 'D' | 'W'; label: string }>;
  };
  loading: {
    isSubmitting: boolean;
    isLoadingPatient?: boolean;
    isSearchingPatient?: boolean;
  };
  onChange: (field: keyof PatientFormViewProps['values'], value: string) => void;
  /** Called when user enters age directly - will calculate DOB */
  onAgeChange?: (age: number) => void;
  onCancel: () => void;
  onSubmit: () => void;
  actionLabels?: { submit: string; cancel?: string };
  isFormValid?: boolean;
  /** Existing patients found with the same phone number */
  existingPatients?: Array<{
    id: string;
    name: string;
    age?: number;
    gender: 'M' | 'F' | 'O' | 'U';
    phoneNumber: string;
  }>;
  /** Show phone field at top with search behavior (for create mode modal) */
  phoneSearchMode?: boolean;
  /** Callback to switch to appointment scheduling for a specific existing patient */
  onSwitchToAppointment?: (patient: {
    id: string;
    name: string;
    phoneNumber: string;
    age?: number;
    gender: 'M' | 'F' | 'O' | 'U';
  }) => void;
  /**
   * Label for the secondary identifier field (e.g. "Book No.").
   * When provided the identifier input is rendered; when undefined the field is hidden.
   */
  identifierLabel?: string;
  /** Organization name to display (read-only) */
  organizationName?: string;
};

export function PatientFormView({
  mode,
  values,
  errors,
  options,
  loading,
  onChange,
  onAgeChange,
  onCancel,
  onSubmit,
  actionLabels,
  isFormValid = true,
  existingPatients,
  phoneSearchMode = false,
  onSwitchToAppointment,
  identifierLabel,
  organizationName,
}: PatientFormViewProps) {
  const hasExistingPatients = existingPatients && existingPatients.length > 0;
  const pageTitle = mode === 'edit' ? 'Edit Patient' : 'Patient Details';
  const pageSubtitle =
    mode === 'edit'
      ? 'Update patient information'
      : "Enter Patient's Details to create an appointment";
  const submitLabel = actionLabels?.submit || 'Create Appointment';
  const cancelLabel = actionLabels?.cancel || 'Cancel';

  // State for age input value
  const [ageValue, setAgeValue] = useState('');

  /**
   * Sanitize weight input: allow only digits and a single decimal point,
   * strip all other characters (e/E/+/-/letters/symbols), and cap at 6 chars
   * (e.g. "500.00") to prevent absurdly long values.
   */
  const sanitizeWeight = (raw: string): string => {
    // Remove anything that isn't a digit or a dot
    let cleaned = raw.replace(/[^0-9.]/g, '');
    // Keep only the first decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    // Limit integer part to 3 digits (max 500) and decimal part to 2 digits
    const [intPart, decPart] = cleaned.split('.');
    const clampedInt = (intPart || '').slice(0, 3);
    const result = decPart !== undefined ? `${clampedInt}.${decPart.slice(0, 2)}` : clampedInt;
    return result;
  };

  /** Block keys that type="text" won't catch but are invalid for weight */
  const blockWeightInvalidKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
  };
  // Handle age input change - just update the age value, independent of DOB
  const handleAgeInputChange = (inputAge: string) => {
    setAgeValue(inputAge);
    const numAge = parseInt(inputAge, 10);
    if (!isNaN(numAge) && numAge >= 0 && numAge <= 150) {
      onAgeChange?.(numAge);
    }
  };

  const fieldsDisabled = loading.isSubmitting;

  if (loading.isLoadingPatient) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[46px] w-full" />
        <Skeleton className="h-[46px] w-full" />
        <Skeleton className="h-[46px] w-full" />
        <Skeleton className="h-[46px] w-full" />
      </div>
    );
  }

  // Render for create mode with phone search (modal style like ScheduleAppointmentModal)
  if (phoneSearchMode && mode === 'create') {
    return (
      <div
        className="max-h-[65vh] overflow-y-auto pr-2"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db #f3f4f6' }}
      >
        {/* Centered Header - matching ScheduleAppointmentModal */}
        <div className="mb-4 text-center">
          <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-gray-700">
            {pageTitle}
          </h2>
          <p className="mx-auto mt-2 w-3/4 font-hanken text-base font-normal leading-label-2 tracking-label-2 text-gray-700">
            {pageSubtitle}
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!loading.isSubmitting) onSubmit();
          }}
          className="space-y-3"
        >
          {/* Organization (read-only) */}
          {organizationName && (
            <div>
              <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
                Organization
              </label>
              <input
                type="text"
                value={organizationName}
                disabled
                className="flex h-[46px] w-full items-center rounded-lg border-0 bg-gray-100 px-3 text-sm text-gray-600 outline-none ring-1 ring-inset ring-gray-200 disabled:cursor-not-allowed"
              />
            </div>
          )}

          {/* Phone Number + Secondary Identifier - side by side */}
          <div className={identifierLabel ? 'grid grid-cols-2 gap-4' : ''}>
            {/* Phone Number - First field with search indicator */}
            <div>
              <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
                Phone Number
                <span className="ml-1 text-destructive" aria-label="required">
                  *
                </span>
              </label>
              <div className="relative">
                <input
                  type="tel"
                  placeholder="Enter 10-digit phone"
                  value={values.phoneNumber}
                  onChange={(e) => {
                    onChange('phoneNumber', sanitizePhoneInput(e.target.value));
                  }}
                  disabled={loading.isSubmitting}
                  className="flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 pr-10 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:opacity-50"
                />
                {/* Search indicator */}
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                  {loading.isSearchingPatient && (
                    <Loader2 className="h-4 w-4 animate-spin text-[#061a61]" />
                  )}
                  {!loading.isSearchingPatient && hasExistingPatients && (
                    <CheckCircle2 className="h-4 w-4 text-amber-500" />
                  )}
                </div>
              </div>
              {errors?.phoneNumber && (
                <p className="mt-1.5 text-sm text-destructive">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Secondary identifier (e.g. Book No.) — only when configured */}
            {identifierLabel && (
              <div>
                <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
                  {formatLabel(identifierLabel)}
                </label>
                <input
                  type="text"
                  placeholder={`Enter ${formatLabel(identifierLabel)}`}
                  value={values.identifier || ''}
                  onChange={(e) => onChange('identifier', e.target.value)}
                  disabled={fieldsDisabled}
                  className="flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
                />
                {errors?.identifier && (
                  <p className="mt-1.5 text-sm text-destructive">{errors.identifier}</p>
                )}
              </div>
            )}
          </div>

          {/* Existing patients found with this phone number */}
          {hasExistingPatients && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="mb-2 font-hanken text-sm font-medium text-amber-800">
                {existingPatients!.length} patient{existingPatients!.length > 1 ? 's' : ''} already
                registered with this number
              </p>
              <div className="space-y-1.5">
                {existingPatients!.map((patient) => (
                  <div
                    key={patient.id}
                    className="flex items-center justify-between rounded-md bg-white p-2 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-hanken text-sm font-medium text-gray-900">
                        {patient.name}
                      </span>
                      <span className="font-hanken text-xs text-gray-700">
                        {formatGenderDisplay(patient.gender)}
                        {patient.age !== undefined && patient.age > 0
                          ? ` / ${patient.age} yrs`
                          : ''}
                      </span>
                    </div>
                    {onSwitchToAppointment && (
                      <button
                        type="button"
                        onClick={() => onSwitchToAppointment(patient)}
                        className="flex items-center gap-1 rounded-md bg-[#061a61] px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-[#061a61]/90"
                      >
                        <CalendarPlus className="h-3 w-3" />
                        Schedule
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <div className="h-px flex-1 bg-amber-200" />
                <span className="font-hanken text-xs text-amber-600">
                  Or register a new patient below
                </span>
                <div className="h-px flex-1 bg-amber-200" />
              </div>
            </div>
          )}

          {/* First Name */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              First Name
              <span className="ml-1 text-destructive" aria-label="required">
                *
              </span>
            </label>
            <input
              type="text"
              placeholder="Enter first name"
              value={values.firstName}
              onChange={(e) => onChange('firstName', e.target.value)}
              disabled={fieldsDisabled}
              className="flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
            />
            {errors?.firstName && (
              <p className="mt-1.5 text-sm text-destructive">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Last Name
              <span className="ml-1 text-destructive" aria-label="required">
                *
              </span>
            </label>
            <input
              type="text"
              placeholder="Enter last name"
              value={values.lastName}
              onChange={(e) => onChange('lastName', e.target.value)}
              disabled={fieldsDisabled}
              className="flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
            />
            {errors?.lastName && (
              <p className="mt-1.5 text-sm text-destructive">{errors.lastName}</p>
            )}
          </div>

          {/* Age and Date of Birth - Side by Side */}
          <AgeDobFields
            variant="modal"
            ageValue={ageValue}
            dateOfBirth={values.dateOfBirth}
            onAgeChange={handleAgeInputChange}
            onDobChange={(value) => onChange('dateOfBirth', value)}
            disabled={fieldsDisabled}
            errors={{ age: errors?.age, dateOfBirth: errors?.dateOfBirth }}
          />

          {/* Weight */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Weight
            </label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="Enter weight in kg"
              value={values.weight || ''}
              onChange={(e) => onChange('weight', sanitizeWeight(e.target.value))}
              onKeyDown={blockWeightInvalidKey}
              disabled={fieldsDisabled}
              maxLength={6}
              className="flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
            />
            {errors?.weight && <p className="mt-1.5 text-sm text-destructive">{errors.weight}</p>}
          </div>

          {/* Gender */}
          <GenderField
            variant="modal"
            value={values.gender}
            options={options.gender}
            onChange={(value) => onChange('gender', value)}
            disabled={fieldsDisabled}
            error={errors?.gender}
          />

          {/* Marital Status */}
          {options.maritalStatus && (
            <MaritalStatusField
              variant="modal"
              value={values.maritalStatus || ''}
              options={options.maritalStatus}
              onChange={(value) => onChange('maritalStatus', value)}
              disabled={fieldsDisabled}
              error={errors?.maritalStatus}
            />
          )}

          {/* Address */}
          <AddressField
            variant="modal"
            value={values.address || ''}
            onChange={(value) => onChange('address', value)}
            disabled={fieldsDisabled}
            error={errors?.address}
          />

          {/* Action Buttons */}
          <div className="space-y-2 pt-2">
            <Button
              type="submit"
              variant="primary"
              loading={loading.isSubmitting}
              disabled={loading.isSubmitting || loading.isSearchingPatient || !isFormValid}
              className="h-[46px] w-full rounded-lg bg-[#061a61] py-0 text-sm font-medium hover:bg-[#061a61]/90"
            >
              {loading.isSubmitting ? 'Creating...' : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // Original render for edit mode and non-modal create
  return (
    <div
      className="max-h-[65vh] overflow-y-auto pr-2"
      style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db #f3f4f6' }}
    >
      {/* Centered Header - matching ScheduleAppointmentModal */}
      <div className="mb-4 text-center">
        <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-gray-700">
          {pageTitle}
        </h2>
        <p className="mx-auto mt-2 w-3/4 font-hanken text-base font-normal leading-label-2 tracking-label-2 text-gray-700">
          {mode === 'edit'
            ? 'Update patient information'
            : 'Enter patient details to create a new record'}
        </p>
      </div>

      {/* Form sections */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!loading.isSubmitting) onSubmit();
        }}
        className="space-y-3"
      >
        {/* Organization (read-only) */}
        {organizationName && (
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Organization
            </label>
            <input
              type="text"
              value={organizationName}
              disabled
              className="flex h-[46px] w-full items-center rounded-lg border-0 bg-gray-100 px-3 text-sm text-gray-600 outline-none ring-1 ring-inset ring-gray-200 disabled:cursor-not-allowed"
            />
          </div>
        )}

        {/* Demographics */}
        <div className="space-y-3">
          {/* First Name */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              First Name
              <span className="ml-1 text-destructive" aria-label="required">
                *
              </span>
            </label>
            <input
              type="text"
              placeholder="Enter first name"
              value={values.firstName}
              onChange={(e) => onChange('firstName', e.target.value)}
              disabled={loading.isSubmitting}
              className="flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
            />
            {errors?.firstName && (
              <p className="mt-1.5 text-sm text-destructive">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Last Name
              <span className="ml-1 text-destructive" aria-label="required">
                *
              </span>
            </label>
            <input
              type="text"
              placeholder="Enter last name"
              value={values.lastName}
              onChange={(e) => onChange('lastName', e.target.value)}
              disabled={loading.isSubmitting}
              className="flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
            />
            {errors?.lastName && (
              <p className="mt-1.5 text-sm text-destructive">{errors.lastName}</p>
            )}
          </div>

          <div className="space-y-3">
            {/* Age and Date of Birth - Side by Side */}
            <AgeDobFields
              variant="modal"
              ageValue={ageValue}
              dateOfBirth={values.dateOfBirth}
              onAgeChange={handleAgeInputChange}
              onDobChange={(value) => onChange('dateOfBirth', value)}
              disabled={loading.isSubmitting}
              errors={{ age: errors?.age, dateOfBirth: errors?.dateOfBirth }}
            />

            {/* Weight */}
            <div>
              <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
                Weight
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="Enter weight in kg"
                value={values.weight || ''}
                onChange={(e) => onChange('weight', sanitizeWeight(e.target.value))}
                onKeyDown={blockWeightInvalidKey}
                disabled={loading.isSubmitting}
                maxLength={6}
                className="flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
              />
              {errors?.weight && <p className="mt-1.5 text-sm text-destructive">{errors.weight}</p>}
            </div>

            {/* Gender */}
            <GenderField
              variant="modal"
              value={values.gender}
              options={options.gender}
              onChange={(value) => onChange('gender', value)}
              disabled={loading.isSubmitting}
              error={errors?.gender}
            />

            {/* Marital Status */}
            {options.maritalStatus && (
              <MaritalStatusField
                variant="modal"
                value={values.maritalStatus || ''}
                options={options.maritalStatus}
                onChange={(value) => onChange('maritalStatus', value)}
                disabled={loading.isSubmitting}
                error={errors?.maritalStatus}
              />
            )}
          </div>

          {/* Phone Number + Secondary Identifier - side by side */}
          <div className={identifierLabel ? 'grid grid-cols-2 gap-4' : ''}>
            <div>
              <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
                Phone Number
                <span className="ml-1 text-destructive" aria-label="required">
                  *
                </span>
              </label>
              <input
                type="tel"
                placeholder="Enter 10-digit phone number"
                value={values.phoneNumber}
                onChange={(e) => {
                  onChange('phoneNumber', sanitizePhoneInput(e.target.value));
                }}
                disabled={loading.isSubmitting}
                className="flex h-[46px] w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
              />
              {errors?.phoneNumber && (
                <p className="mt-1.5 text-sm text-destructive">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Secondary identifier (e.g. Book No.) — only when configured */}
            {identifierLabel && (
              <div>
                <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
                  {formatLabel(identifierLabel)}
                </label>
                <input
                  type="text"
                  placeholder={`Enter ${formatLabel(identifierLabel)}`}
                  value={values.identifier || ''}
                  onChange={(e) => onChange('identifier', e.target.value)}
                  disabled={loading.isSubmitting}
                  className="flex h-10 w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 placeholder:text-[#969aac] focus:ring-2 focus:ring-[#061a61] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
                />
                {errors?.identifier && (
                  <p className="mt-1.5 text-sm text-destructive">{errors.identifier}</p>
                )}
              </div>
            )}
          </div>

          {/* Address */}
          <AddressField
            variant="modal"
            value={values.address || ''}
            onChange={(value) => onChange('address', value)}
            disabled={loading.isSubmitting}
            error={errors?.address}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={loading.isSubmitting}
            className="flex-1"
          >
            {cancelLabel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading.isSubmitting}
            disabled={loading.isSubmitting || !isFormValid}
            className="flex-1 bg-[#061a61] hover:bg-[#061a61]/90"
          >
            {loading.isSubmitting
              ? mode === 'edit'
                ? 'Updating...'
                : 'Creating...'
              : actionLabels?.submit || (mode === 'edit' ? 'Update Patient' : 'Create Patient')}
          </Button>
        </div>
      </form>
    </div>
  );
}
